// Integración con FluxAI — plataforma de telemetría AMI de Alebas Ingeniería (misma dueña).
// Solar Hub diseña/implementa el sistema PV; FluxAI lo monitorea con datos reales (M&V medido vs
// simulado). Este cliente abre la consola de monitoreo de FluxAI para una cotización ganada.
//
// v1: deep-link a la consola. El SSO real (sesión compartida sin re-login) llega con el login
// server-side de api/ (capa FL-4 del ROADMAP-FLUXAI). Cuando el handoff (FL-1) complete, la
// cotización trae `flux_installation_id` y el link apunta directo al equipo.

import { getToken } from './auth';

/**
 * ¿Es la sesión de cliente que firma Solar Hub con su llave propia (`iss: solarhub-cliente`)? Se
 * resuelve acá y no se importa: este módulo solo necesita leer el `iss`, y depender de más de
 * `auth` lo acopla a todo lo que ese módulo arrastra.
 */
function esSesionPropia(token) {
  try {
    const p = String(token || '').split('.')[1];
    if (!p) return false;
    const c = JSON.parse(atob(p.replace(/-/g, '+').replace(/_/g, '/')));
    return Boolean(c) && c.iss === 'solarhub-cliente';
  } catch { return false; }
}
import { currentTheme } from '../constants';

const RAW = (process.env.REACT_APP_FLUXAI_URL || 'https://www.fluxai.solutions').replace(/\/+$/, '');

export const fluxaiBaseUrl = () => RAW;
export const fluxaiConfigured = () => RAW.length > 0;

// Puente SSO de la suite: adjunta el token de la sesión de Solar Hub al deep-link de FluxAI
// para reanudar la MISMA sesión SIN re-login. Si el token está FIRMADO (api/ desplegado) →
// SSO duro. Si es el token demo (alg:none) → FluxAI lo acepta SOLO para correos allowlisted
// (puente demo, ver `auth.ts::ingestSsoToken`), derivando el rol del allowlist. Así el cliente
// no tiene que volver a iniciar sesión en el servicio embebido.
function conSso(url) {
  const t = getToken();
  // La sesión de cliente de Solar Hub (llave propia) NO viaja a FluxAI: no la aceptaría y es una
  // credencial que no tiene por qué quedar en la URL ni en los logs de otro dominio.
  if (!t || esSesionPropia(t)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}sso=${encodeURIComponent(t)}`;
}

/** El tema viaja con el salto de app para que las dos consolas no cambien de claro↔oscuro. */
function conTema(url) {
  let tema = 'dark';
  try { tema = currentTheme() === 'light' ? 'light' : 'dark'; } catch { /* default oscuro */ }
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}theme=${tema}`;
}

/**
 * ¿Va a reanudar sesión el deep-link, o FluxAI va a pedir login?
 *
 * Espejo EXACTO de `isSignedToken` de FluxAI (`web/src/lib/auth.ts`), que es el criterio con el
 * que su `ingestSsoToken` decide: token FIRMADO → se ingiere tal cual (SSO duro); token SIN firma
 * → lo acepta **solo** para los correos de su allowlist (admin demo · soporte · clientes demo) y
 * para cualquier otro devuelve `false` → sin sesión → su `AuthGuard` manda a `/login`.
 *
 * Lo que Solar Hub SÍ puede saber es si su propio token está firmado; la allowlist vive en el env
 * de FluxAI y **no se adivina** — por eso el estado `demo` describe la consecuencia sin afirmar
 * que el correo del usuario esté o no en ella.
 */
export function ssoEstado() {
  const t = getToken();
  if (!t) return { puente: 'sin_sesion', reanuda: false };
  // Firmada, pero con la llave propia de Solar Hub: no se adjunta, así que FluxAI pedirá login.
  if (esSesionPropia(t)) return { puente: 'demo', reanuda: false };
  const partes = String(t).split('.');
  if (partes.length < 3 || !partes[2]) return { puente: 'demo', reanuda: false };
  try {
    const h = JSON.parse(atob(partes[0].replace(/-/g, '+').replace(/_/g, '/')));
    if (h && h.alg && h.alg !== 'none') return { puente: 'firmado', reanuda: true };
  } catch { /* header ilegible → se trata como no firmado */ }
  return { puente: 'demo', reanuda: false };
}

/**
 * Ficha de un equipo en FluxAI (`/equipos/:id`). Sin id → el listado `/equipos`.
 * Nunca el dashboard: Solar Hub no abre la consola AMI (SCADA/Mitú).
 * Adjunta SSO si hay token.
 */
export function fluxaiEquipoUrl(deviceId) {
  const id = deviceId == null || deviceId === '' ? '' : String(deviceId);
  if (!id) return conSso(`${RAW}/equipos`);
  return conSso(`${RAW}/equipos/${encodeURIComponent(id)}`);
}

/**
 * URL de monitoreo en FluxAI para una cotización.
 * Con `flux_installation_id` → ficha. Sin él → listado `/equipos`, no el dashboard.
 */
export function fluxaiMonitoringUrl(quote) {
  const dev = quote?.flux_installation_id || quote?.payload?.flux_installation_id;
  return fluxaiEquipoUrl(dev);
}

// Ruta de cada servicio PRESTADO desde FluxAI (nivel máquina). Los SKUs son los del
// catálogo único de la suite (catalog.js / SQL 95). Se abre con la MISMA sesión vía SSO
// cuando hay token firmado (api/ desplegado); si no, va a la consola para iniciar sesión.
const FLUXAI_SERVICE_ROUTES = {
  monitoreo_premium: '/equipos',
  informe_iso: '/informes',
  reactiva: '/reactiva',
  scada: '/scada',
  facturacion: '/facturas',
  red: '/red',
  billing_api: '/integraciones',
  personalizacion_documentos: '/mi-cuenta',
  soporte_prioritario: '/alex',
};

/**
 * Deep-link al servicio de FluxAI por SKU, con la MISMA sesión (SSO). `opts.embed=true`
 * añade `embed=1` → FluxAI oculta su shell y muestra SOLO el contenido del servicio (sin
 * doble menú), y `theme` iguala el modo claro/oscuro de Solar Hub. El embebido lo usa
 * `FluxaiEmbed`; "Abrir en pestaña" usa la app completa (sin embed).
 */
/** URL de la app COMPLETA de FluxAI con la sesión adjunta (SSO) — para el lanzador de la suite
 *  (cambiar de app reanudando sesión sin re-login). Cada app conserva su propia PWA. */
export function fluxaiAppUrl() {
  return conSso(conTema(RAW));
}

export function fluxaiServiceUrl(sku, { embed = false } = {}) {
  const path = FLUXAI_SERVICE_ROUTES[sku] || '/equipos';
  let q = '';
  if (embed) {
    let tema = 'dark';
    try { tema = currentTheme(); } catch { /* default oscuro */ }
    q = `?embed=1&theme=${tema}`;
  }
  return conSso(`${RAW}${path}${q}`);
}

/**
 * ALEX embebido — la ruta `/alex` de FluxAI a pantalla completa, con la MISMA sesión (SSO) y el
 * tema de Solar Hub. Es el CEREBRO ÚNICO de la suite (`AgenteFluxAI`): un solo asistente con la
 * misma memoria/RAG/herramientas de gestión, sin duplicar cerebros ni re-login. `AlexLauncher` lo
 * abre en un panel/iframe; sin CORS porque la página vive en fluxai.solutions y usa su propio proxy.
 */
export function fluxaiAlexUrl({ embed = true } = {}) {
  let q = '';
  if (embed) {
    let tema = 'dark';
    try { tema = currentTheme(); } catch { /* default oscuro */ }
    q = `?embed=1&theme=${tema}`;
  }
  return conSso(`${RAW}/alex${q}`);
}
